import { isServerRendering } from '@farmasiet/frontendcore-reactcomponents';
import * as React from 'react';

import { IProductTrackingData } from '../../Catalog/Product/IProductTrackingData.csharp';
import { ProductViewModel } from '../../Catalog/Product/ViewModels/ProductViewModel.csharp';
import { SearchResultProductViewModel } from '../../Filtering/Api/SearchResultProductViewModel.csharp';
import { ProductCardViewModel } from '../../Partials/ProductCard/ProductCardViewModel.csharp';
import { CartItemViewModel } from '../../Purchase/Cart/Item/ViewModels/CartItemViewModel.csharp';
import { TrackingData } from '../FacebookConversionApi/TrackingData.csharp';
import { GoogleAnalytics4Tracker } from './GoogleAnalytics4/GoogleAnalytics4Tracker';
import { GoogleTagManagerDataLayer } from './GoogleTagManagerDataLayer';
import { GTMClickType } from './GTMClickType';
import { IGoogleTagManagerAnalyticsTracker } from './IGoogleTagManagerAnalyticsTracker';
import { IGoogleTagManagerNonAnalyticsTracker } from './IGoogleTagManagerNonAnalyticsTracker';
import { ITrackingData } from './ITrackingData';
import { MouseButtons } from './MouseButtonsEnum';
import { NonAnalyticsTracker } from './NonAnalyticsTracker/NonAnalyticsTracker';
import { PromotionImpressionViewModel } from './PromotionImpression/PromotionImpressionViewModel.csharp';
import { SearchVariant } from './SearchVariant';

export class GoogleTagManager {
    private static readonly _analyticsTracker: IGoogleTagManagerAnalyticsTracker[] = [new GoogleAnalytics4Tracker()];
    private static readonly _nonAnalyticsTracker: IGoogleTagManagerNonAnalyticsTracker[] = [new NonAnalyticsTracker()];
    private static readonly _dataLayer = new GoogleTagManagerDataLayer();

    private static maxProductImpressionsAmount = 4;
    private static maxPromoImpressionsAmount = 3;
    private static productCards: ProductCardViewModel[] = [];
    private static searchResults: SearchResultProductViewModel[] = [];
    private static promotions: PromotionImpressionViewModel[] = [];

    public static initOnBeforeUnloadEventListener() {
        window.addEventListener('beforeunload', () => {
            this.pushProductImpressionsToDataLayer();
            this.pushPromoImpressionsToDataLayer();
        });
    }

    public static productDetails(product: ProductViewModel, originName: string, promotionId?: string, promotionName?: string) {
        this._analyticsTracker.map((tracker) => tracker.trackItemView(product, originName, promotionId, promotionName));
    }

    public static productImpression(product: ProductCardViewModel) {
        this.productCards.push(product);

        if (this.productCards.length >= this.maxProductImpressionsAmount) {
            this.pushProductImpressionsToDataLayer();
        }
    }

    public static searchProductImpression(product: SearchResultProductViewModel) {
        this.searchResults.push(product);

        if (this.searchResults.length >= this.maxProductImpressionsAmount) {
            this.pushProductImpressionsToDataLayer();
        }
    }

    public static productCardClick(e: React.MouseEvent<HTMLAnchorElement>, product: ProductCardViewModel, productLink: string) {
        e.preventDefault();
        this._analyticsTracker.map((tracker) => tracker.trackProductCardItemSelect(product, productLink, this.getClickType(e)));
    }

    public static cartItemClick(e: React.MouseEvent<HTMLAnchorElement>, product: CartItemViewModel) {
        e.preventDefault();
        this._analyticsTracker.map((tracker) => tracker.trackCartItemItemSelect(product, product.link, this.getClickType(e)));
    }

    public static searchItemClick(e: React.MouseEvent<HTMLAnchorElement>, product: SearchResultProductViewModel) {
        e.preventDefault();
        this._analyticsTracker.map((tracker) => tracker.trackSearchItemSelect(product, product.link, this.getClickType(e)));
    }

    public static addToCart(
        product: IProductTrackingData,
        quantity: number,
        trackingData: TrackingData,
        originName?: string,
        promotionId?: string,
        promotionName?: string
    ) {
        this._analyticsTracker.map((tracker) => tracker.trackAddToCart(product, quantity, trackingData.eventId, originName, promotionId, promotionName));
    }

    public static addToWishList(product: IProductTrackingData) {
        this._analyticsTracker.map((tracker) => tracker.trackAddToWishList(product));
    }

    public static removeFromCart(product: CartItemViewModel, quantity: number) {
        this._analyticsTracker.map((tracker) => tracker.trackRemoveFromCart(product, quantity));
    }

    public static changeQuantity(product: CartItemViewModel, newQuantity: number, oldQuantity: number) {
        if (newQuantity > oldQuantity) {
            this._analyticsTracker.map((tracker) => tracker.trackAddToCart(product, newQuantity - oldQuantity));
        } else if (newQuantity < oldQuantity) {
            this._analyticsTracker.map((tracker) => tracker.trackRemoveFromCart(product, oldQuantity - newQuantity));
        }
    }

    public static beginCheckout(products: CartItemViewModel[], actionField: ITrackingData) {
        this._analyticsTracker.map((tracker) => tracker.trackBeginCheckout(products, actionField));
    }

    public static addShippingInfo(products: CartItemViewModel[], actionField: ITrackingData) {
        this._analyticsTracker.map((tracker) => tracker.trackAddShippingInfo(products, actionField));
    }

    public static addPaymentInfo(products: CartItemViewModel[], actionField: ITrackingData, trackingData?: TrackingData) {
        this._analyticsTracker.map((tracker) => tracker.trackAddPaymentInfo(products, actionField, trackingData?.eventId));
    }

    public static purchase(products: CartItemViewModel[], actionField: ITrackingData, trackingId: string) {
        this._analyticsTracker.map((tracker) => tracker.trackPurchase(products, actionField, trackingId));
    }

    public static priceSpiderPurchase(products: CartItemViewModel[]) {
        this._nonAnalyticsTracker.map((tracker) => tracker.trackPriceSpiderPurchase(products));
    }

    public static promotionImpression(promotion: PromotionImpressionViewModel) {
        this.promotions.push(promotion);

        if (this.promotions.length >= this.maxPromoImpressionsAmount) {
            this.pushPromoImpressionsToDataLayer();
        }
    }

    public static searchSuggestion(searchTerm: string) {
        this._analyticsTracker.map((tracker) => tracker.trackSearchSuggestions(searchTerm));
    }

    public static viewSearchResults(searchTerm: string, variant: SearchVariant) {
        this._analyticsTracker.map((tracker) => tracker.trackSearchResultsView(searchTerm, variant));
    }

    public static applySearchTag(tagValue: string, tagIndex: number) {
        this._analyticsTracker.map((tracker) => tracker.trackSearchTagClick(tagValue, tagIndex));
    }

    public static promotionClick(e: React.MouseEvent, promotion: PromotionImpressionViewModel, originalUrl?: string) {
        e.preventDefault();
        this._analyticsTracker.map((tracker) => tracker.trackPromotionSelect(promotion, originalUrl ?? promotion.link, this.getClickType(e)));
    }

    public static cartView(items: CartItemViewModel[], total: number, couponCode: string, isMiniCart?: boolean) {
        if (items && items.length > 0) {
            this._analyticsTracker.map((tracker) => tracker.trackViewCart(items, total, couponCode, isMiniCart));
        }
    }

    public static addUserData(userId?: string, hashedEmail?: string) {
        this._dataLayer.addUserData(userId, hashedEmail);
    }

    private static getClickType(e: React.MouseEvent): GTMClickType {
        const isMiddleMouseButton = e.button === MouseButtons.MiddleButton;

        return {
            isNewTab: e.ctrlKey || e.metaKey || isMiddleMouseButton,
            isMiddleMouseButton
        };
    }

    private static pushProductImpressionsToDataLayer() {
        if (this.productCards.length > 0) {
            this._analyticsTracker.map((tracker) => tracker.trackProductCardsItemImpressions(this.productCards));
            this.productCards = [];
        }

        if (this.searchResults.length > 0) {
            this._analyticsTracker.map((tracker) => tracker.trackSearchResultsItemImpressions(this.searchResults));
            this.searchResults = [];
        }
    }

    private static pushPromoImpressionsToDataLayer() {
        if (this.promotions.length > 0) {
            this._analyticsTracker.map((tracker) => tracker.trackPromotionsView(this.promotions));
            this.promotions = [];
        }
    }
}

if (!isServerRendering()) {
    GoogleTagManager.initOnBeforeUnloadEventListener();
}
